import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FormProvider, { RHFTextField } from '../../../components/hook-form';
import { dayOfPickerValue, pickerValue } from './holidayDays';

const labelSx = { opacity: 0.5, mb: 0.5 };

function formValues(holiday) {
  return {
    name: holiday?.name ?? '',
    start: pickerValue(holiday?.startDay),
    end: pickerValue(holiday?.endDay),
    isRecurring: Boolean(holiday?.isRecurring),
  };
}

/*
 * The add and edit dialog. `holiday` is null to add one, or the row to edit with its
 * `startDay` and `endDay`. `onSave` gets `{ name, startDay, endDay, isRecurring }` and
 * throws an Error with the message to show when the save fails.
 */
export default function HolidayDialog({ open, holiday, onClose, onSave }) {
  const [error, setError] = useState(null);
  const methods = useForm({ defaultValues: formValues(holiday) });
  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (open) {
      reset(formValues(holiday));
      setError(null);
    }
  }, [open, holiday, reset]);

  const onSubmit = async (values) => {
    const name = values.name.trim();
    const startDay = dayOfPickerValue(values.start);
    const endDay = dayOfPickerValue(values.end);
    if (!name) return setError('Enter a holiday name.');
    if (!startDay || !endDay) return setError('Choose a start date and an end date.');
    if (endDay < startDay) return setError('The end date must be on or after the start date.');

    try {
      await onSave({ name, startDay, endDay, isRecurring: values.isRecurring });
    } catch (saveError) {
      setError(saveError.message);
    }
    return undefined;
  };

  const renderDay = (name, label) => (
    <Box>
      <Typography variant="subtitle1" sx={labelSx}>
        {label}
      </Typography>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <DatePicker
            value={field.value}
            onChange={field.onChange}
            views={['year', 'month', 'day']}
            openTo="day"
            format="DD-MM-YYYY"
          />
        )}
      />
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={{ pb: 2 }}>{holiday ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>

      <DialogContent sx={{ typography: 'body2' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Typography variant="subtitle1" sx={labelSx}>
            Holiday Name
          </Typography>
          <RHFTextField name="name" placeholder="Enter Holiday Name" sx={{ mb: 2 }} />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              columnGap={2}
              rowGap={2}
              display="grid"
              gridTemplateColumns={{ xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' }}
              sx={{ mb: 2 }}
            >
              {renderDay('start', 'Start Date')}
              {renderDay('end', 'End Date')}
            </Box>
          </LocalizationProvider>

          <Controller
            name="isRecurring"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                labelPlacement="start"
                label="Repeat Yearly"
                control={
                  <Switch checked={field.value} onChange={(_, checked) => field.onChange(checked)} />
                }
              />
            )}
          />

          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
              Save
            </LoadingButton>
          </DialogActions>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
